// src/components/Dashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { tradingApi } from '../services/api';

// ✅ Debug logging right after imports
console.log('🔍 Dashboard - tradingApi object:', tradingApi);
console.log('🔍 Dashboard - checkSubscription method:', tradingApi?.checkSubscription);
console.log('🔍 Dashboard - Available methods:', tradingApi ? Object.getOwnPropertyNames(Object.getPrototypeOf(tradingApi)) : 'tradingApi is undefined');

function InvestDashboard() {
  const [user, setUser] = useState(null);
  const [pendingTrades, setPendingTrades] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [stats, setStats] = useState({
    totalTrades: 0,
    successRate: 0,
    totalProfit: 0,
    activePositions: 0
  });

  const userId = localStorage.getItem('userId');

  const showDebugInfo = () => {
    const debugData = {
      userId: localStorage.getItem('userId'),
      email: localStorage.getItem('userEmail'),
      accountNumber: localStorage.getItem('accountNumber'),
      googleScriptUrl: process.env.REACT_APP_GOOGLE_SCRIPT_URL,
      tradingApiAvailable: !!tradingApi,
      checkSubscriptionAvailable: typeof tradingApi?.checkSubscription === 'function',
      apiMethods: tradingApi ? Object.getOwnPropertyNames(Object.getPrototypeOf(tradingApi)) : [],
      allLocalStorage: { ...localStorage }
    };
    setDebugInfo(debugData);
    console.log('Debug Info:', debugData);
  };

  const loadDashboardData = useCallback(async () => {
    try {
      setError(null);
      console.log('🔄 Loading ALL data from Google Sheets for userId:', userId);
      
      // ✅ Validate API object before making calls
      if (!tradingApi || typeof tradingApi.checkSubscription !== 'function') {
        throw new Error('Trading API not properly loaded. Please refresh the page.');
      }
      
      // Fetch ALL data from Google Sheets with detailed logging
      const results = await Promise.allSettled([
        (async () => {
          console.log('📡 Calling getUserSettings...');
          return await tradingApi.getUserSettings(userId);
        })(),
        (async () => {
          console.log('📡 Calling checkSubscription...');
          return await tradingApi.checkSubscription(userId);
        })(),
        (async () => {
          console.log('📡 Calling getPendingTrades...');
          return await tradingApi.getPendingTrades(userId);
        })(),
        (async () => {
          console.log('📡 Calling getSystemStatus...');
          return await tradingApi.getSystemStatus();
        })()
      ]);

      console.log('📊 Google Sheets API Results:', results);

      // Handle user settings
      if (results[0].status === 'fulfilled') {
        const userResult = results[0].value;
        console.log('👤 User settings response:', userResult);
        
        if (userResult?.success === true && userResult?.data) {
          console.log('✅ User data loaded successfully:', userResult.data);
          setUser(userResult.data);
          
          if (userResult.data.stats) {
            setStats(userResult.data.stats);
          } else {
            setStats({
              totalTrades: userResult.data.totalTrades || 0,
              successRate: 0,
              totalProfit: 0,
              activePositions: 0
            });
          }
        } else {
          console.error('❌ User settings failed:', userResult);
          setError(`Failed to load user data: ${userResult?.message || 'Unknown error'}`);
        }
      } else {
        console.error('❌ User settings request failed:', results[0].reason);
        setError(`Google Sheets connection failed: ${results[0].reason?.message || 'Connection error'}`);
      }

      // Handle subscription
      if (results[1].status === 'fulfilled') {
        const subscriptionResult = results[1].value;
        console.log('💎 Subscription response:', subscriptionResult);
        
        if (subscriptionResult?.success && subscriptionResult?.data?.subscription) {
          setSubscription(subscriptionResult.data.subscription);
        }
      } else {
        console.error('❌ Subscription check failed:', results[1].reason);
      }

      // Handle pending trades
      if (results[2].status === 'fulfilled') {
        const tradesResult = results[2].value;
        console.log('⚡ Pending trades response:', tradesResult);
        
        if (tradesResult?.success && tradesResult?.data?.trades) {
          setPendingTrades(tradesResult.data.trades);
          
          setStats(prev => ({
            ...prev,
            activePositions: tradesResult.data.trades.length
          }));
        } else {
          setPendingTrades([]);
        }
      } else {
        console.error('❌ Pending trades failed:', results[2].reason);
      }

      // Handle system status
      if (results[3].status === 'fulfilled') {
        const systemResult = results[3].value;
        console.log('🔧 System status response:', systemResult);
        
        if (systemResult?.success && systemResult?.data) {
          setSystemStatus(systemResult.data);
        }
      } else {
        console.error('❌ System status failed:', results[3].reason);
      }

      const failedRequests = results.filter(r => r.status === 'rejected');
      if (failedRequests.length === 0) {
        console.log('✅ All Google Sheets requests completed successfully!');
      }

    } catch (error) {
      console.error('❌ Error loading Google Sheets data:', error);
      setError(`Failed to load data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log('Dashboard mounted with userId:', userId);
    console.log('🔍 tradingApi available:', !!tradingApi);
    console.log('🔍 checkSubscription method available:', typeof tradingApi?.checkSubscription);
    
    if (!userId) {
      setError('No User ID found. Please re-register.');
      setLoading(false);
      return;
    }

    // ✅ Validate API object before using it
    if (!tradingApi) {
      setError('Trading API not loaded. Please refresh the page.');
      setLoading(false);
      return;
    }

    if (typeof tradingApi.checkSubscription !== 'function') {
      setError('Trading API methods not available. Please check your connection and refresh the page.');
      setLoading(false);
      return;
    }

    loadDashboardData();
    
    const interval = setInterval(loadDashboardData, 30000);
    
    return () => clearInterval(interval);
  }, [userId, loadDashboardData]);



  const fixUserData = async () => {
    try {
      console.log('🔧 Attempting to fix user data...');
      setLoading(true);
      
      if (!tradingApi || typeof tradingApi.fixAll !== 'function') {
        throw new Error('Trading API fixAll method not available');
      }
      
      const result = await tradingApi.fixAll();
      console.log('Fix result:', result);
      
      if (result.success) {
        const correctUserId = result.data?.summary?.userID;
        if (correctUserId) {
          localStorage.setItem('userId', correctUserId);
          localStorage.setItem('userEmail', 'adiec23@outlook.com');
          localStorage.setItem('accountNumber', '110982793');
          
          console.log('✅ User data fixed, reloading dashboard...');
          alert('✅ User data fixed successfully! Reloading...');
          await loadDashboardData();
        }
      } else {
        alert('❌ Fix failed: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Fix failed:', error);
      alert('❌ Fix failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      console.log('🔄 Testing connection...');
      setLoading(true);
      
      if (!tradingApi || typeof tradingApi.testConnection !== 'function') {
        throw new Error('Trading API testConnection method not available');
      }
      
      const result = await tradingApi.testConnection();
      
      if (result.success) {
        console.log('✅ Connection successful!');
        alert('✅ Connection successful! Reloading data...');
        await loadDashboardData();
      } else {
        console.error('❌ Connection failed:', result.message);
        alert('❌ Connection failed: ' + result.message);
      }
    } catch (error) {
      console.error('❌ Connection test error:', error);
      alert('❌ Connection test error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const runEmergencyFix = async () => {
    try {
      console.log('🚨 Running emergency fix...');
      setLoading(true);
      
      if (!tradingApi || typeof tradingApi.emergencyFix !== 'function') {
        throw new Error('Trading API emergencyFix method not available');
      }
      
      const result = await tradingApi.emergencyFix();
      console.log('Emergency fix result:', result);
      
      if (result.success) {
        alert('🚨 Emergency fix completed! Check console for details.');
        await loadDashboardData();
      } else {
        alert('❌ Emergency fix failed: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Emergency fix error:', error);
      alert('❌ Emergency fix error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const testUserLookup = async () => {
    try {
      console.log('👤 Testing user lookup...');
      setLoading(true);
      
      if (!tradingApi || typeof tradingApi.testUser !== 'function') {
        throw new Error('Trading API testUser method not available');
      }
      
      const result = await tradingApi.testUser();
      console.log('User test result:', result);
      
      if (result.success) {
        alert('👤 User lookup test completed! Check console for details.');
        
        if (result.data?.correctUserID) {
          localStorage.setItem('userId', result.data.correctUserID);
          await loadDashboardData();
        }
      } else {
        alert('❌ User test failed: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ User test error:', error);
      alert('❌ User test error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const runFullDiagnostic = async () => {
    console.log('🔍 Running full diagnostic...');
    
    const tests = [
      {
        name: 'API Object Check',
        test: () => {
          if (!tradingApi) throw new Error('tradingApi is undefined');
          if (typeof tradingApi.checkSubscription !== 'function') throw new Error('checkSubscription method missing');
          if (typeof tradingApi.getUserSettings !== 'function') throw new Error('getUserSettings method missing');
          if (typeof tradingApi.getPendingTrades !== 'function') throw new Error('getPendingTrades method missing');
          if (typeof tradingApi.getSystemStatus !== 'function') throw new Error('getSystemStatus method missing');
          return { 
            available: true, 
            methods: Object.getOwnPropertyNames(Object.getPrototypeOf(tradingApi)),
            checkSubscription: typeof tradingApi.checkSubscription,
            getUserSettings: typeof tradingApi.getUserSettings,
            getPendingTrades: typeof tradingApi.getPendingTrades,
            getSystemStatus: typeof tradingApi.getSystemStatus
          };
        }
      },
      {
        name: 'Environment Variables',
        test: () => {
          const url = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
          if (!url) throw new Error('REACT_APP_GOOGLE_SCRIPT_URL not set');
          return { url, hasUrl: true };
        }
      },
      {
        name: 'Basic Connection Test',
        test: async () => {
          if (!tradingApi.testConnection) throw new Error('testConnection method not available');
          const result = await tradingApi.testConnection();
          if (!result.success) throw new Error(result.message);
          return result;
        }
      },
      {
        name: 'System Status Test',
        test: async () => {
          if (!tradingApi.getSystemStatus) throw new Error('getSystemStatus method not available');
          const result = await tradingApi.getSystemStatus();
          return result;
        }
      },
      {
        name: 'User Data Test',
        test: async () => {
          const testUserId = localStorage.getItem('userId') || 'test';
          if (!tradingApi.debugUser) throw new Error('debugUser method not available');
          const result = await tradingApi.debugUser(testUserId);
          return result;
        }
      }
    ];

    const results = {};
    
    for (const test of tests) {
      try {
        console.log(`🧪 Testing: ${test.name}`);
        const result = await test.test();
        console.log(`✅ ${test.name}:`, result);
        results[test.name] = { success: true, result };
      } catch (error) {
        console.error(`❌ ${test.name}:`, error);
        results[test.name] = { success: false, error: error.message };
      }
    }
    
    const passed = Object.values(results).filter(r => r.success).length;
    const total = Object.keys(results).length;
    console.log(`\n📊 DIAGNOSTIC SUMMARY: ${passed}/${total} tests passed`);
    
    // Show detailed results
    console.table(Object.entries(results).reduce((acc, [name, result]) => {
      acc[name] = {
        Status: result.success ? '✅ PASS' : '❌ FAIL',
        Error: result.error || 'None'
      };
      return acc;
    }, {}));
    
    if (passed === total) {
      alert('🎉 All tests passed! System is working correctly.');
    } else {
      alert(`⚠️ ${total - passed} tests failed. Check console for detailed results.`);
    }
    
    return results;
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      await loadDashboardData();
    } catch (error) {
      console.error('❌ Refresh failed:', error);
      setError(`Refresh failed: ${error.message}`);
    }
  };

  const getSubscriptionStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'expiring_soon': return 'text-yellow-400';
      case 'expired': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getSubscriptionStatusBadge = (status) => {
    switch (status) {
      case 'active': return '🟢 Active';
      case 'expiring_soon': return '🟡 Expiring Soon';
      case 'expired': return '🔴 Expired';
      default: return '⚪ Unknown';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-blue-500 border-t-transparent rounded-full mb-4"
        />
        <p className="text-white text-sm">Loading data from Google Sheets...</p>
        <p className="text-gray-400 text-xs mt-2">User ID: {userId}</p>
        <p className="text-gray-500 text-xs mt-1">
          API Status: {tradingApi ? '✅ Loaded' : '❌ Not Loaded'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="px-4 py-3 sm:py-4 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full">
                <span className="text-white font-bold text-sm sm:text-base">⚡ VON SCALPER</span>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-white text-lg sm:text-xl font-bold">Welcome back!</h1>
                <p className="text-gray-400 text-xs sm:text-sm truncate max-w-xs">{user?.email || 'Loading...'}</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
              {/* API Status Indicator */}
              <div className={`flex items-center gap-2 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm ${
                tradingApi ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                  tradingApi ? 'bg-green-400' : 'bg-red-400'
                }`}></div>
                <span className="hidden sm:inline">API</span>
                <span>{tradingApi ? 'Ready' : 'Error'}</span>
              </div>

              {/* System Status */}
              <div className={`flex items-center gap-2 px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm ${
                systemStatus?.systemEnabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                  systemStatus?.systemEnabled ? 'bg-green-400' : 'bg-red-400'
                }`}></div>
                <span className="hidden sm:inline">System</span>
                <span>{systemStatus?.systemEnabled ? 'Online' : 'Offline'}</span>
              </div>

              {/* Subscription Status */}
              <div className={`px-2 py-1 sm:px-3 rounded-full text-xs sm:text-sm bg-black/30 ${getSubscriptionStatusColor(subscription?.status)}`}>
                <span className="hidden sm:inline">{getSubscriptionStatusBadge(subscription?.status)}</span>
                <span className="sm:hidden">
                  {subscription?.status === 'active' ? '🟢' : 
                   subscription?.status === 'expiring_soon' ? '🟡' : 
                   subscription?.status === 'expired' ? '🔴' : '⚪'}
                </span>
              </div>
            </div>
          </div>

          {/* Mobile Email Display */}
          <div className="sm:hidden mt-2">
            <h1 className="text-white text-lg font-bold">Welcome back!</h1>
            <p className="text-gray-400 text-sm truncate">{user?.email || 'Loading...'}</p>
          </div>
        </div>
      </header>

      <div className="px-4 py-4 sm:py-8 max-w-7xl mx-auto">
        {/* Enhanced Error Display */}
        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3 sm:p-4">
            <p className="text-red-400 text-sm mb-3">🔗 {error}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button 
                onClick={refreshData}
                className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-blue-700 transition-all"
              >
                🔄 Retry
              </button>
              <button 
                onClick={testConnection}
                className="bg-green-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-green-700 transition-all"
              >
                🔗 Test API
              </button>
              <button 
                onClick={fixUserData}
                className="bg-purple-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-purple-700 transition-all"
              >
                🔧 Fix Data
              </button>
              <button 
                onClick={runEmergencyFix}
                className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-red-700 transition-all"
              >
                🚨 Emergency
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              <button 
                onClick={testUserLookup}
                className="bg-yellow-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-yellow-700 transition-all"
              >
                👤 Test User
              </button>
              <button 
                onClick={showDebugInfo}
                className="bg-gray-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-gray-700 transition-all"
              >
                🐛 Debug
              </button>
              <button 
                onClick={() => window.open(process.env.REACT_APP_GOOGLE_SCRIPT_URL, '_blank')}
                className="bg-indigo-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-indigo-700 transition-all"
              >
                📜 Open Script
              </button>
            </div>
          </div>
        )}

        {/* Success Message */}
        {user && !error && (
          <div className="mb-4 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
            <p className="text-green-400 text-sm">✅ Connected to Google Sheets successfully</p>
          </div>
        )}

        {/* Enhanced Debug Info Display */}
        {debugInfo && (
          <div className="mb-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 sm:p-4">
            <h3 className="text-yellow-400 font-bold mb-2">📊 Debug Information</h3>
            <div className="space-y-2 mb-3">
              <div className="text-yellow-300 text-sm">
                <span className="font-semibold">Google Apps Script URL:</span>
                <br />
                <span className="font-mono text-xs break-all">{debugInfo.googleScriptUrl || 'Not set'}</span>
              </div>
              <div className="text-yellow-300 text-sm">
                <span className="font-semibold">User ID:</span> {debugInfo.userId || 'Not set'}
              </div>
              <div className="text-yellow-300 text-sm">
                <span className="font-semibold">Email:</span> {debugInfo.email || 'Not set'}
              </div>
              <div className="text-yellow-300 text-sm">
                <span className="font-semibold">Account:</span> {debugInfo.accountNumber || 'Not set'}
              </div>
              <div className="text-yellow-300 text-sm">
                <span className="font-semibold">Trading API Available:</span> {debugInfo.tradingApiAvailable ? '✅ Yes' : '❌ No'}
              </div>
              <div className="text-yellow-300 text-sm">
                <span className="font-semibold">checkSubscription Available:</span> {debugInfo.checkSubscriptionAvailable ? '✅ Yes' : '❌ No'}
              </div>
              <div className="text-yellow-300 text-sm">
                <span className="font-semibold">API Methods:</span> {debugInfo.apiMethods.join(', ')}
              </div>
            </div>
            <pre className="text-yellow-300 text-xs overflow-auto max-h-40 bg-black/20 p-2 rounded">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
            <button 
              onClick={() => setDebugInfo(null)}
              className="mt-2 text-yellow-400 text-sm underline hover:no-underline"
            >
              Hide Debug Info
            </button>
          </div>
        )}

        {/* Enhanced Google Apps Script Quick Actions Panel */}
        <div className="mb-4 bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 sm:p-4">
          <h3 className="text-blue-400 font-bold mb-3 flex items-center gap-2">
            <span>📜</span>
            Google Apps Script Actions
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button 
              onClick={testConnection}
              className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-blue-700 transition-all"
            >
              🔗 Test Connection
            </button>
            <button 
              onClick={fixUserData}
              className="bg-green-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-green-700 transition-all"
            >
              🔧 Fix User Data
            </button>
            <button 
              onClick={testUserLookup}
              className="bg-purple-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-purple-700 transition-all"
            >
              👤 Test User Lookup
            </button>
            <button 
              onClick={runEmergencyFix}
              className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-red-700 transition-all"
            >
              🚨 Emergency Fix
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            <button 
              onClick={runFullDiagnostic}
              className="bg-indigo-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-indigo-700 transition-all"
            >
              🔍 Full Diagnostic
            </button>
            <button 
              onClick={showDebugInfo}
              className="bg-gray-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-gray-700 transition-all"
            >
              🐛 Debug Info
            </button>
            <button 
              onClick={refreshData}
              className="bg-teal-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-teal-700 transition-all"
            >
              🔄 Refresh All
            </button>
            <button 
              onClick={() => window.open(process.env.REACT_APP_GOOGLE_SCRIPT_URL, '_blank')}
              className="bg-yellow-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm hover:bg-yellow-700 transition-all"
            >
              📜 Open Script
            </button>
          </div>
          <div className="mt-2 text-gray-400 text-xs">
            Quick access to Google Apps Script debugging and fixing functions
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 sm:mb-8">
          <div className="flex overflow-x-auto bg-black/20 p-1 rounded-xl backdrop-blur-xl" style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none'
          }}>
            <style jsx>{`
              div::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            <div className="flex space-x-1 min-w-max">
              {[
                { id: 'overview', label: 'Overview', icon: '📊', shortLabel: 'Home' },
                { id: 'trades', label: 'Active Trades', icon: '⚡', shortLabel: 'Trades' },
                { id: 'history', label: 'Trade History', icon: '📈', shortLabel: 'History' },
                { id: 'settings', label: 'Settings', icon: '⚙️', shortLabel: 'Settings' }
              ].map((tab) => (
                <motion.button
                  key={tab.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center gap-2 py-2.5 px-4 sm:px-6 rounded-lg font-medium transition-all duration-200 whitespace-nowrap text-sm sm:text-base ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <span className="text-sm sm:text-base">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden text-xs">{tab.shortLabel}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4 sm:space-y-6"
            >
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                {[
                  { title: 'Total Trades', value: stats?.totalTrades || 0, icon: '📊', color: 'blue' },
                  { title: 'Success Rate', value: `${stats?.successRate || 0}%`, icon: '🎯', color: 'green' },
                  { title: 'Total Profit', value: `$${stats?.totalProfit || 0}`, icon: '💰', color: 'emerald' },
                  { title: 'Active Positions', value: stats?.activePositions || 0, icon: '⚡', color: 'purple' }
                ].map((stat, index) => (
                  <motion.div
                    key={stat.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-black/20 backdrop-blur-xl rounded-xl p-3 sm:p-6 border border-white/10"
                  >
                    <div className="flex items-center justify-between mb-2 sm:mb-4">
                      <span className="text-2xl sm:text-4xl">{stat.icon}</span>
                      <div className={`w-6 h-6 sm:w-12 sm:h-12 bg-gradient-to-r from-${stat.color}-500 to-${stat.color}-600 rounded-full flex items-center justify-center`}>
                        <span className="text-white font-bold text-xs sm:text-sm">{index + 1}</span>
                      </div>
                    </div>
                    <h3 className="text-gray-400 text-xs sm:text-sm mb-1 sm:mb-2">{stat.title}</h3>
                    <p className="text-white text-lg sm:text-2xl font-bold">{stat.value}</p>
                  </motion.div>
                ))}
              </div>

              {/* Subscription Info */}
              <div className="bg-black/20 backdrop-blur-xl rounded-xl p-4 sm:p-6 border border-white/10">
                <h2 className="text-white text-lg sm:text-xl font-bold mb-4 flex items-center gap-2">
                  <span>💎</span>
                  Subscription Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">Status</p>
                    <p className={`font-semibold text-sm sm:text-base ${getSubscriptionStatusColor(subscription?.status)}`}>
                      {getSubscriptionStatusBadge(subscription?.status)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Expires</p>
                    <p className="text-white font-semibold text-sm sm:text-base">
                      {subscription?.expiryDate ? new Date(subscription.expiryDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Days Remaining</p>
                    <p className="text-white font-semibold text-sm sm:text-base">{subscription?.daysRemaining || 0} days</p>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-black/20 backdrop-blur-xl rounded-xl p-4 sm:p-6 border border-white/10">
                <h2 className="text-white text-lg sm:text-xl font-bold mb-4 flex items-center gap-2">
                  <span>📈</span>
                  Recent Activity
                </h2>
                <div className="space-y-3">
                  {pendingTrades && pendingTrades.length > 0 ? (
                    pendingTrades.slice(0, 5).map((trade, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                            trade.action === 'BUY' ? 'bg-green-400' : 'bg-red-400'
                          }`}></div>
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-medium text-sm sm:text-base truncate">{trade.symbol}</p>
                            <p className="text-gray-400 text-xs sm:text-sm">{trade.action} {trade.lotSize} lots</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-white font-medium text-sm sm:text-base">@{trade.entryPrice}</p>
                          <p className="text-gray-400 text-xs sm:text-sm">{new Date(trade.timestamp).toLocaleTimeString()}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl sm:text-6xl mb-2">📭</div>
                      <p className="text-gray-400 text-sm sm:text-base">No recent activity</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Active Trades Tab */}
          {activeTab === 'trades' && (
            <motion.div
              key="trades"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-black/20 backdrop-blur-xl rounded-xl p-4 sm:p-6 border border-white/10"
            >
              <h2 className="text-white text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
                <span>⚡</span>
                Active Trades ({pendingTrades?.length || 0})
              </h2>
              
              {!pendingTrades || pendingTrades.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="text-4xl sm:text-6xl mb-4">📭</div>
                  <p className="text-gray-400 text-base sm:text-lg">No active trades</p>
                  <p className="text-gray-500 text-sm mt-2">New signals will appear here automatically</p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {pendingTrades.map((trade, index) => (
                    <motion.div
                      key={trade.tradeId || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-800/30 rounded-xl p-3 sm:p-4 border border-gray-700/50"
                    >
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                          <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0 ${
                            trade.action === 'BUY' ? 'bg-green-400' : 'bg-red-400'
                          }`}></div>
                          <h3 className="text-white font-bold text-base sm:text-lg truncate">{trade.symbol}</h3>
                          <span className={`px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-xs sm:text-sm font-medium flex-shrink-0 ${
                            trade.action === 'BUY' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {trade.action}
                          </span>
                        </div>
                        <span className="text-gray-400 text-xs flex-shrink-0">
                          {trade.timestamp ? new Date(trade.timestamp).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-sm">
                        <div>
                          <p className="text-gray-400 text-xs">Lot Size</p>
                          <p className="text-white font-semibold">{trade.lotSize || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs">Entry Price</p>
                          <p className="text-white font-semibold">{trade.entryPrice || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs">Stop Loss</p>
                          <p className="text-red-400 font-semibold">{trade.stopLoss || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs">Take Profit</p>
                          <p className="text-green-400 font-semibold">{trade.takeProfit || 'N/A'}</p>
                        </div>
                      </div>
                      
                      <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-xs">R:R Ratio:</span>
                          <span className="text-blue-400 font-semibold text-sm">{trade.rrRatio || 'N/A'}</span>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs self-start sm:self-auto ${
                          trade.urgency === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                          trade.urgency === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {trade.urgency || 'NORMAL'} Priority
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4 sm:space-y-6"
            >
              <div className="bg-black/20 backdrop-blur-xl rounded-xl p-4 sm:p-6 border border-white/10">
                <h2 className="text-white text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
                  <span>⚙️</span>
                  Trading Settings
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Lot Multiplier</label>
                    <input
                      type="number"
                      value={user?.lotMultiplier || 1.0}
                      min="0.01"
                      max="2.0"
                      step="0.01"
                      className="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-white text-sm sm:text-base"
                      readOnly
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Max Concurrent Trades</label>
                    <input
                      type="number"
                      value={user?.maxTrades || 10}
                      min="1"
                      max="20"
                      className="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-white text-sm sm:text-base"
                      readOnly
                    />
                  </div>
                </div>
                
                <p className="mt-4 text-gray-400 text-sm">
                  Settings are managed through Google Sheets. Contact support to modify.
                </p>
              </div>

              <div className="bg-black/20 backdrop-blur-xl rounded-xl p-4 sm:p-6 border border-white/10">
                <h2 className="text-white text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
                  <span>📊</span>
                  Account Information
                </h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm">User ID</p>
                      <p className="text-white font-mono bg-gray-800/30 p-2 rounded text-xs sm:text-sm break-all">{userId}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Email</p>
                      <p className="text-white bg-gray-800/30 p-2 rounded text-xs sm:text-sm">{user?.email || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Account Number</p>
                      <p className="text-white font-mono bg-gray-800/30 p-2 rounded text-xs sm:text-sm">{user?.accountNumber || 'Not set'}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-gray-400 text-sm">Broker</p>
                    <p className="text-white text-sm sm:text-base">{user?.broker || 'Not set'}</p>
                  </div>
                  
                  <div>
                    <p className="text-gray-400 text-sm">Registration Date</p>
                    <p className="text-white text-sm sm:text-base">
                      {user?.registrationDate ? new Date(user.registrationDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Trade History Tab */}
          {activeTab === 'history' && (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-black/20 backdrop-blur-xl rounded-xl p-4 sm:p-6 border border-white/10"
            >
              <h2 className="text-white text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
                <span>📈</span>
                Trade History
              </h2>
              
              <div className="text-center py-8 sm:py-12">
                <div className="text-4xl sm:text-6xl mb-4">📊</div>
                <p className="text-gray-400 text-base sm:text-lg">Trade history coming soon</p>
                <p className="text-gray-500 text-sm mt-2">Detailed trading analytics and performance metrics</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default InvestDashboard;